import React, { useId, useMemo } from "react";
import { useInstagramApiPublishingOn } from "../../../../../utils/feature-toggle-utils";

import { RadioGroup, RadioGroupItem } from "../../../../ui/radio-group";
import { FIELDS } from "../../../../../constants/PostFormFields";
// Leaf modules rather than the entities barrel, which closes an import cycle
// back through the post form.
import { getIGApiPublishingStatusOn } from "../../../../../entities/accounts/libs/get-account-statuses";
import { isInstagramAccount } from "../../../../../entities/accounts/libs/account-platform";
import { isVideoMedia } from "../../../../../utils/media";
import type { PostMediaFile } from "../../../../../shared/types/post";
import type { AccountPlatformType } from "../../../../../shared/types/accounts";
import {
  INSTAGRAM_ONLY,
  PlatformLabelBadges,
} from "../PlatformLabelBadges/PlatformLabelBadges";

const FEED_AND_REELS = "feed";
const REELS_ONLY = "reels";

interface InstagramPlacementControlProps {
  mediaFiles: PostMediaFile[];
  selectedPlatform: AccountPlatformType | "LINKED";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formAccounts: any[];
  updateField: (field: string, value: boolean) => void;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost?: any;
}

/**
 * Whether this media publishes to Instagram as a reel.
 *
 * Mirrors the server's `resolvePublishTarget`: one media file that is a video,
 * with the story check made by each caller. Exported so the reel-only controls
 * cannot drift apart from each other.
 */
export const isInstagramReelMedia = (files: unknown): boolean =>
  Array.isArray(files) && files.length === 1 && isVideoMedia(files[0]);

/**
 * The Instagram accounts this post will actually publish to.
 *
 * On a linked post the account list spans every platform, so filtering by
 * capability alone would let a Facebook-only selection show the control.
 *
 * Uses `isInstagramAccount` rather than reading `platformType` directly: an
 * ABSENT `platformType` means Instagram in this codebase, and production
 * Instagram rows leave it unset.
 */
export const selectedInstagramAccounts = (formAccounts: any[]) =>
  (formAccounts ?? []).filter((account) => isInstagramAccount(account));

/**
 * True when one placement cannot speak for the whole selection.
 *
 * The control writes a single value that every selected Instagram account
 * publishes with, so it is only honest while the selection agrees with itself.
 * Two ways it stops agreeing:
 *
 * - Capability. A phone-path account ignores the choice entirely, so a mixed
 *   selection would show a control that only half the accounts obey. This is
 *   the branch that fires in practice.
 * - Media. `mediaFiles` is the SHARED media, while an unlinked platform tab
 *   carries its own on `platformPost`. When those disagree the shared media
 *   says reel and the post Instagram actually publishes is something else.
 *
 * **The media branch cannot fire today**, and the docblock previously implied it
 * could. `PlatformField` declares only `unlinked`, and the form reducer handles
 * media in its own case that writes the shared `mediaFiles` and returns before
 * reaching the per-platform override path. So the two references are always
 * equal and `isInstagramReelMedia(mediaFiles)` decides alone.
 *
 * Kept rather than deleted because it costs one comparison and becomes live the
 * moment a platform tab carries its own media, which is a change nobody would
 * think to audit this file for. Its tests exercise a shape the app cannot
 * currently produce, and say so.
 */
export const isMixedPlacementSelection = ({
  igAccounts,
  mediaFiles,
  platformMediaFiles,
  apiPublishingOn,
}: {
  igAccounts: any[];
  mediaFiles: unknown;
  platformMediaFiles: unknown;
  /** SKE-4733. The workspace's API publishing gate. */
  apiPublishingOn: boolean;
}): boolean => {
  if (
    !igAccounts.every((account) =>
      getIGApiPublishingStatusOn(account, apiPublishingOn)
    )
  ) {
    return true;
  }
  if (!isInstagramReelMedia(mediaFiles)) return true;
  if (platformMediaFiles !== undefined && platformMediaFiles !== mediaFiles) {
    return !isInstagramReelMedia(platformMediaFiles);
  }
  return false;
};

/**
 * Meta publishes no default for `share_to_feed`, so the server applies ours.
 * Reading it the same way here keeps a saved post opening on the choice it will
 * actually publish with: an explicit value wins, and a post the customer marked
 * as a reel stays Reels-only.
 *
 * The server owns the fallback. Writing it eagerly here raced the reel toggle,
 * which sets `videoPostType` in the same commit, so a new short vertical video
 * latched "Feed and Reels" before it had been marked a reel.
 */
export const placementFromPost = (platformPost: any): string => {
  const explicit = platformPost?.instagram?.shareToFeed;
  if (typeof explicit === "boolean") {
    return explicit ? FEED_AND_REELS : REELS_ONLY;
  }
  return platformPost?.videoPostType === "reel" ? REELS_ONLY : FEED_AND_REELS;
};

/**
 * Where an Instagram video appears once published.
 *
 * Meta removed the standalone video media type in November 2023, so every
 * Instagram video is a reel and the only remaining choice is whether it also
 * shows in the feed. This is deliberately separate from the Facebook reel
 * toggle: that one writes `videoPostType`, which is shared across platforms,
 * and Facebook's reel rules are its own.
 *
 * Only offered when every selected Instagram account can publish through the
 * API. The phone path has no such control, so showing it there would promise
 * something we cannot deliver.
 */
export const InstagramPlacementControl: React.FC<
  InstagramPlacementControlProps
> = ({
  mediaFiles,
  selectedPlatform,
  formAccounts,
  updateField,
  disabled,
  platformPost,
}) => {
  const igAccounts = useMemo(
    () => selectedInstagramAccounts(formAccounts),
    [formAccounts]
  );

  const apiPublishingOn = useInstagramApiPublishingOn();
  const fieldId = useId();
  const feedId = `${fieldId}-feed`;
  const reelsId = `${fieldId}-reels`;

  const show = useMemo(() => {
    if (selectedPlatform !== "IG" && selectedPlatform !== "LINKED")
      return false;
    if (platformPost?.isStory) return false;
    if (igAccounts.length === 0) return false;
    return !isMixedPlacementSelection({
      igAccounts,
      mediaFiles,
      platformMediaFiles: platformPost?.mediaFiles,
      apiPublishingOn,
    });
  }, [
    apiPublishingOn,
    selectedPlatform,
    platformPost?.isStory,
    platformPost?.mediaFiles,
    mediaFiles,
    igAccounts,
  ]);

  const value = placementFromPost(platformPost);

  if (!show) return null;

  const optionClassName = disabled
    ? "flex cursor-not-allowed items-center gap-2 font-inter text-sm leading-5 text-neutral-500"
    : "flex cursor-pointer items-center gap-2 font-inter text-sm leading-5 text-neutral-800";

  return (
    <div className="mb-4 flex flex-col gap-2" data-testid="ig-placement">
      <div className="flex items-center gap-2">
        <span
          className="font-inter text-sm font-medium leading-5 text-neutral-800"
          id={`${fieldId}-label`}
        >
          Where this reel appears
        </span>
        <PlatformLabelBadges
          selectedPlatform={selectedPlatform}
          platforms={INSTAGRAM_ONLY}
        />
      </div>
      <RadioGroup
        className="flex flex-row gap-4"
        value={value}
        disabled={disabled}
        aria-labelledby={`${fieldId}-label`}
        onValueChange={(next: string) =>
          updateField(FIELDS.IG_SHARE_TO_FEED, next === FEED_AND_REELS)
        }
      >
        <label className={optionClassName} htmlFor={feedId}>
          <RadioGroupItem value={FEED_AND_REELS} id={feedId} />
          Feed and Reels
        </label>
        <label className={optionClassName} htmlFor={reelsId}>
          <RadioGroupItem value={REELS_ONLY} id={reelsId} />
          Reels tab only
        </label>
      </RadioGroup>
    </div>
  );
};

export default InstagramPlacementControl;
